import { DiagnosisRegistrationStoreData } from '@/pages/demands/contexts/diagnosis-registration-store'
import { getURLToUploadBatchFiles } from '@/services/get-url-to-upload-batch-files'
import {
  getURLToUploadFile,
  InputFileType,
} from '@/services/get-url-to-upload-file'

interface FileToGetSignedUrlBase {
  type: InputFileType
  inputType: keyof NonNullable<
    DiagnosisRegistrationStoreData['selectFilesData']
  >
}

export interface FileToGetSignedUrl extends FileToGetSignedUrlBase {
  file: File | Blob
  resumable: boolean
  originalFilename: string
}

interface BatchFileToGetSignedUrl extends FileToGetSignedUrlBase {
  files: File[]
  originalNames: string[]
}

export interface SignedUrlsQueueProps {
  registerData: DiagnosisRegistrationStoreData['registerData']
}

export class SignedUrlsQueue {
  private filesToGetSignedUrl: FileToGetSignedUrl[] = []
  private batchFilesToGetSignedUrl: BatchFileToGetSignedUrl[] = []

  private registerData

  constructor(props: SignedUrlsQueueProps) {
    this.registerData = props.registerData
  }

  enqueue(file: FileToGetSignedUrl) {
    this.filesToGetSignedUrl.push(file)
  }

  enqueueBatchFiles(batchFiles: BatchFileToGetSignedUrl) {
    this.batchFilesToGetSignedUrl.push(batchFiles)
  }

  async generateSignedUrls(demandId: string, signal: AbortSignal) {
    const { culture, treatment } = this.registerData!

    const filesToUpload = (
      await Promise.all([
        ...this.filesToGetSignedUrl.map(async (fileReference) => {
          const url = await getURLToUploadFile(
            treatment,
            culture,
            demandId,
            fileReference,
            { signal },
          )
          return { ...fileReference, url, isBatch: false }
        }),
        ...this.batchFilesToGetSignedUrl.map(async (fileReference) => {
          const urls = await getURLToUploadBatchFiles(
            treatment,
            culture,
            demandId,
            fileReference.type,
            fileReference.originalNames,
            fileReference.inputType,
            { signal },
          )
          return fileReference.files.map((file, index) => ({
            file,
            type: fileReference.type,
            inputType: fileReference.inputType,
            resumable: false,
            originalFilename: fileReference.originalNames[index],
            url: urls[index].url,
            isBatch: true,
          }))
        }),
      ])
    )
      .flat()
      .map((file) => ({
        ...file,
        done: false,
      }))

    return filesToUpload
  }
}
